import "../assets/scss/App.scss";

import * as React from "react";
import { HashRouter, Route, Switch } from "react-router-dom";

import { Config } from "../Config";
import { DictionaryService } from "../services/dictionary/DictionaryService";
import { IImageService } from "../services/image/IImageService";
import { ImageService } from "../services/image/ImageService";
import { DummyWordService } from "../services/word/DummyWordService";
import { WordService } from "../services/word/WordService";
import { DictionaryStore } from "../stores/dictionary/DictionaryStore";
import { ILoanWordsStore, LoanWordsStore, WordFilter } from "../stores/word/LoanWordsStore";
import { Word } from "../stores/word/Word";
import { DomUtils } from "../utils/DomUtils";
import { Filter } from "./Filter";
import { Map } from "./Map";
import { Orientation } from "./Orientation";
import { PortraitFrame } from "./PortraitFrame";
import { TransparentWordList } from "./TransparentWordList";
import { WordListWithButtons } from "./WordListWithButtons";

export interface AppProps {}

export default class App extends React.Component<AppProps, undefined> {
  constructor(props: AppProps, context: any) {
    super(props, context);

    const wordService = Config.isDummyData
      ? new DummyWordService()
      : new WordService(Config.getGoogleSheetsUrl());

    this.wordStore = new LoanWordsStore();

    wordService.getWords(words => this.wordStore.setWords(words.filter(w => !!w.originalWord))); // CSV parsing can return a dummy row at end

    this.dictionaryStore = new DictionaryStore(new DictionaryService());
    this.imageService = new ImageService();
  }

  private wordStore: ILoanWordsStore;
  private dictionaryStore: DictionaryStore;
  private imageService: IImageService;

  render() {
    return (
      <HashRouter>
        <Switch>
          <Route
            path="/filter/:originalLanguage/:originalWord"
            component={this.filterRoute}
          />
          <Route path="/filter//:originalWord" component={this.filterRoute} />
          <Route
            path="/filter/:originalLanguage"
            component={this.filterRoute}
          />
          <Route path="/" component={this.filterRoute} />
        </Switch>
      </HashRouter>
    );
  }

  private buildFilter = (match: any): WordFilter => {
    const language = match.params.originalLanguage;
    const word = match.params.originalWord;

    return {
      originalLanguage: language,
      originalWord: word
    };
  };

  private filterRoute = ({ match }) => {
    return this.renderAppWithFilter(this.buildFilter(match));
  };

  private renderAppWithFilter = (filter: WordFilter) => {
    return (
      <Orientation.DeviceOrientation>
        <Orientation.Orientation orientation={"landscape"} alwaysRender={false}>
          {this.renderForLandscape(filter)}
        </Orientation.Orientation>
        <Orientation.Orientation orientation={"portrait"} alwaysRender={false}>
          {this.renderForPortrait(filter)}
        </Orientation.Orientation>
      </Orientation.DeviceOrientation>
    );
  };

  private renderForLandscape = (filter: WordFilter) => {
    return (
      <div className="app">
        <div className="sidePanelColumn" ref={this.bindHeaderContainer}>
          {this.renderSidePanel(filter, true)}
        </div>

        <div className="mainColumn">
          <div className="mapAndList">
            <div className="map">{this.renderMap(filter)}</div>
            <div className="list">{this.renderTransparentWordList(filter)}</div>
          </div>
        </div>
      </div>
    );
  };

  private bindHeaderContainer = (element: HTMLDivElement) => {
    if (element) {
      DomUtils.setAllParentsToFullHeight(element);
    }
  };

  private renderSidePanel = (filter: WordFilter, includeHeader: boolean) => {
    return (
      <Filter
        filter={filter}
        includeHeader={includeHeader}
        wordStore={this.wordStore}
      />
    );
  };

  private renderMap = (filter: WordFilter) => {
    return (
      <Map
        wordStore={this.wordStore}
        dictionaryStore={this.dictionaryStore}
        filter={filter}
        onClick={this.onClickCountryInMap}
        imageService={this.imageService}
      />
    );
  };

  private renderWordList = (filter: WordFilter) => {
    return (
      <WordListWithButtons
        wordStore={this.wordStore}
        filter={filter}
        onClickOriginalWord={this.onClickOriginalWord}
      />
    );
  };

  private renderForPortrait = (filter: WordFilter) => {
    return (
      <PortraitFrame
        filter={filter}
        wordStore={this.wordStore}
        dictionaryStore={this.dictionaryStore}
        imageService={this.imageService}
        renderSidePanel={this.renderSidePanel}
        renderWordList={this.renderWordList}
      />
    );
  };

  private renderTransparentWordList = (filter: WordFilter) => {
    return (
      <TransparentWordList
        wordStore={this.wordStore}
        filter={filter}
        onClickOriginalWord={this.onClickOriginalWord}
      />
    );
  };

  private onClickCountryInMap = (country: string | null) => {
    // some countries have multiple languages -> so pick a random one:
    const language = country
      ? this.wordStore.getRandomLanguageFromCountry(country)
      : null;

    // note: null language -> navigates to 'all'
    this.navigateToWord(language, null);
  };

  private onClickOriginalWord = (word: Word) => {
    this.navigateToWord(word.originalLanguage, word.originalWord);
  };

  private navigateToWord(
    originalLanguage: string | null,
    originalWord: string | null
  ) {
    if (originalWord && !originalLanguage) {
      throw new Error("bad args - language MUST be set, if word is set");
    }

    let url = window.location.href;

    const languagePart = originalLanguage ? `/${originalLanguage}` : "";

    const wordPart = originalWord ? `/${originalWord}` : "";

    url =
      url.substr(0, url.indexOf("#") + 1) + `/filter${languagePart}${wordPart}`;

    window.location.href = url;
  }
}



// WEBPACK FOOTER //
// ./components/App.tsx